import { useI18n } from "@/i18n/I18nProvider";
import { createPortal } from "react-dom";
import { X, Bell, Calendar, ExternalLink } from "lucide-react";
import { useAlerts } from "@/hooks/useAlerts";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";

export default function NotificationOverlay({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { lang, isRtl } = useI18n();
  const { user } = useAuth();
  const { alerts, readIds, markAsRead } = useAlerts(user?.id);

  if (!isOpen) return null;

  const filteredAlerts = alerts.filter(alert => {
    if (alert.isActive === false) return false;
    if (alert.status === 'brouillon') return false;
    if (alert.status === 'programme' && alert.scheduledFor) {
      if (new Date() < new Date(alert.scheduledFor)) return false;
    }
    return true;
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-center items-center md:justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-300">
      <div className={`w-full max-w-md h-full md:h-[calc(100%-2rem)] md:m-4 md:rounded-2xl bg-background shadow-2xl border flex flex-col animate-in ${isRtl ? 'slide-in-from-left' : 'slide-in-from-right'} duration-300 relative overflow-hidden`}>
        <div className="flex items-center justify-between p-4 border-b border-border bg-card/50 backdrop-blur">
          <h2 className="text-xl font-display font-bold flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary animate-pulse" />
            {lang === 'zgh' ? "ⵉⵏⵖⵎⵉⵙⵏ" : isRtl ? "الإشعارات" : "Notifications"}
          </h2>
          <button 
            type="button"
            onClick={onClose} 
            className="p-2 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors outline-none focus:ring-2 focus:ring-primary bg-background shadow-sm border border-border"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {filteredAlerts.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {lang === 'zgh' ? "ⵓⵔ ⵍⵍⵉⵏⵜ ⵜⵏⵖⵎⵉⵙⵉⵏ." : isRtl ? "لا توجد إشعارات." : "Aucune notification."}
            </div>
          ) : (
            filteredAlerts.map(alert => {
              const isRead = readIds.includes(alert.id);
              
              return (
                <div 
                  key={alert.id}
                  onClick={() => markAsRead(alert.id)}
                  className={`p-4 rounded-xl border transition-all cursor-pointer hover:shadow-md ${isRead ? 'bg-muted/20 border-transparent opacity-80' : 'bg-card border-primary/20 shadow-sm'}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Badge variant={isRead ? "secondary" : "default"} className="text-[10px] font-body bg-primary/10 text-primary hover:bg-primary/20 border-none">
                      {alert.theme}
                    </Badge>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-mono">
                      <Calendar className="w-3 h-3" /> {alert.date}
                    </span>
                  </div>
                  <h3 className={`text-sm font-bold mb-1 ${isRead ? 'text-foreground/80' : 'text-foreground'}`}>
                    {alert.title[lang] || alert.title.fr}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    {alert.description[lang] || alert.description.fr}
                  </p>
                  
                  {alert.linkUrl ? (
                     <a href={alert.linkUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                        <ExternalLink className="w-3.5 h-3.5" />
                        {lang === 'zgh' ? "ⴽⵛⵎ" : isRtl ? "دخول" : "Accéder"}
                     </a>
                  ) : (
                     <button className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors">
                        {lang === 'zgh' ? "ⵖⵔ ⵓⴳⴳⴰⵔ" : isRtl ? "اقرأ المزيد" : "Lire la suite"}
                     </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
